import { Button, Modal } from '@palmetto/palmetto-components';
import { Form, Formik, FormikHelpers, FormikValues } from 'formik';
import { ReactNode, SyntheticEvent, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ProgramType } from 'types';

import { BulkEditAddersFormSection } from './BulkEditAddersFormSection';
import { BulkEditConsumerSolarAddersFormSection } from './BulkEditConsumerSolarAddersFormSection';
import { BulkEditConsumerSolarPricingFormSection } from './BulkEditConsumerSolarPricingFormSection';
import { BulkEditEnergyBackupAddersFormSection } from './BulkEditEnergyBackupAddersFormSection';
import { BulkEditEnergyBackupPricingFormSection } from './BulkEditEnergyBackupPricingFormSection';
import { BulkEditModifiersFormSection } from './BulkEditModifiersFormSection';
import { BulkEditPricingFormSection } from './BulkEditPricingFormSection';

const dataKeyToFormMap: Record<string, ReactNode> = {
  priceSheetName: <BulkEditPricingFormSection />,
  escalationRateMin: <BulkEditPricingFormSection />,
  escalationRateMax: <BulkEditPricingFormSection />,
  pvOnlyMin: <BulkEditPricingFormSection />,
  pvOnlyCeiling: <BulkEditPricingFormSection />,
  zeroEscalatorCeiling: <BulkEditPricingFormSection />,
  absoluteCeiling: <BulkEditPricingFormSection />,
  constructionAdders: <BulkEditAddersFormSection />,
  modifiers: <BulkEditModifiersFormSection />,
};

const consumerSolarDataKeyToFormMap: Record<string, ReactNode> = {
  priceSheetName: <BulkEditConsumerSolarPricingFormSection />,
  escalationRateMin: <BulkEditConsumerSolarPricingFormSection />,
  escalationRateMax: <BulkEditConsumerSolarPricingFormSection />,
  minSystemSize: <BulkEditConsumerSolarPricingFormSection />,
  maxSystemSize: <BulkEditConsumerSolarPricingFormSection />,
  minProductionFactor: <BulkEditConsumerSolarPricingFormSection />,
  maxProductionFactor: <BulkEditConsumerSolarPricingFormSection />,
  constructionAdders: <BulkEditConsumerSolarAddersFormSection />,
};

const energyBackupDataKeyToFormMap: Record<string, ReactNode> = {
  priceSheetName: <BulkEditEnergyBackupPricingFormSection />,
  escalationRateMin: <BulkEditEnergyBackupPricingFormSection />,
  escalationRateMax: <BulkEditEnergyBackupPricingFormSection />,
  minTerm: <BulkEditEnergyBackupPricingFormSection />,
  maxTerm: <BulkEditEnergyBackupPricingFormSection />,
  minSystemCost: <BulkEditEnergyBackupPricingFormSection />,
  maxSystemCost: <BulkEditEnergyBackupPricingFormSection />,
  constructionAdders: <BulkEditEnergyBackupAddersFormSection />,
};

interface BulkEditModalProps {
  isModalOpen: boolean;
  dataKey: string;
  closeModal: (event?: SyntheticEvent) => void;
  setEditValues: (values: any) => void;
  initialValues: Record<string, string>;
}

export const BulkEditModal = ({
  isModalOpen,
  dataKey,
  closeModal,
  setEditValues,
  initialValues,
}: BulkEditModalProps) => {
  const { programType } = useParams<{ programType: string }>();
  const isEnergyBackup = programType === ProgramType.energyBackup;
  const isConsumerSolar = programType === ProgramType.consumerSolar;

  const onSubmit = useCallback(
    async (values: FormikValues, { setSubmitting, resetForm }: FormikHelpers<FormikValues>) => {
      try {
        setEditValues(values);
        resetForm();
        closeModal();
      } catch (e) {
        console.error(e);
      } finally {
        setSubmitting(false);
      }
    },
    [closeModal, setEditValues],
  );
  const title = useMemo(
    () =>
      dataKey === 'modifiers' ? 'Modifiers' : dataKey === 'constructionAdders' ? 'Construction Adders' : 'Pricing',
    [dataKey],
  );

  const formSection = useMemo(() => {
    const formMap = isEnergyBackup
      ? energyBackupDataKeyToFormMap
      : isConsumerSolar
        ? consumerSolarDataKeyToFormMap
        : dataKeyToFormMap;
    return formMap[dataKey];
  }, [dataKey, isEnergyBackup, isConsumerSolar]);

  return (
    <Modal ariaLabelledBy="bulkEdit" isOpen={isModalOpen} onDismiss={closeModal} maxWidth="500px">
      <Modal.Header id="bulkEdit" title={`Bulk Edit ${title}`} onDismiss={closeModal} />
      <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {({ resetForm, isSubmitting }) => {
          return (
            <Form>
              <Modal.Body background="secondary">{formSection}</Modal.Body>
              <Modal.Footer>
                <Button
                  as="button"
                  onClick={() => {
                    resetForm();
                    closeModal();
                  }}
                  variant="secondary"
                  tone="neutral"
                  size="md"
                  isLoading={isSubmitting}
                >
                  Cancel
                </Button>
                <Button variant="primary" size="md" type="submit" isLoading={isSubmitting}>
                  Done
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
